import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box, Container, Grid, Typography, Stack,
} from '@mui/material';
import {
  ShieldOutlined, AutoFixHighOutlined, EnergySavingsLeafOutlined,
} from '@mui/icons-material';

export const AuthLayout: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#071321',
        backgroundImage: `
          radial-gradient(circle at 75% 20%, rgba(35, 105, 160, 0.22), transparent 45%),
          radial-gradient(circle at 20% 80%, rgba(57, 214, 176, 0.05), transparent 40%),
          linear-gradient(rgba(120, 180, 220, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(120, 180, 220, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '100% 100%, 100% 100%, 48px 48px, 48px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 3, md: 6 },
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" justifyContent="center">
          
          {/* ── Left Hero / Brand Panel ── */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ pr: { md: 4 } }}>
              {/* Logo */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, cursor: 'pointer' }}
                onClick={() => navigate('/dashboard')}
              >
                <Box component="img" src="/static/logo.svg" alt="KodeGauge" sx={{ height: 38, filter: 'brightness(0) invert(1)' }} />
              </Box>

              {/* Tagline */}
              <Typography variant="h3" fontWeight={800} sx={{ color: '#F8FAFC', letterSpacing: '-0.02em', mb: 2, lineHeight: 1.15 }}>
                Repository Intelligence &amp; AI-Driven Code Health
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8', fontSize: '1.05rem', lineHeight: 1.6, mb: 4, maxWidth: 500 }}>
                Automate technical debt discovery, security exposure auditing, and instant AI refactoring PRs across your entire engineering organization.
              </Typography>

              {/* Feature Highlights */}
              <Stack spacing={2.5} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(57, 214, 176, 0.12)', border: '1px solid rgba(57, 214, 176, 0.28)', color: '#39D6B0', display: 'flex' }}>
                    <ShieldOutlined sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#F8FAFC' }}>
                      Real-time Vulnerability &amp; CVE Auditing
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.25 }}>
                      Dollar-value risk quantification for code-level security issues and dependency CVEs.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.28)', color: '#38BDF8', display: 'flex' }}>
                    <AutoFixHighOutlined sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#F8FAFC' }}>
                      Automated AI Refactoring PRs
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.25 }}>
                      One-click automated fix generation pushed directly to your GitHub pull requests.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.28)', color: '#10B981', display: 'flex' }}>
                    <EnergySavingsLeafOutlined sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#F8FAFC' }}>
                      Green Software Carbon Profiling
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.25 }}>
                      Estimated CO₂ reduction metrics based on Green Software Foundation SCI standards.
                    </Typography>
                  </Box>
                </Box>
              </Stack>

              {/* Stat badges */}
              {/*<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 1, borderTop: '1px solid rgba(120, 180, 220, 0.12)' }}>*/}
              {/*  <Chip*/}
              {/*    icon={<CheckCircle sx={{ fontSize: '14px !important', color: '#39D6B0' }} />}*/}
              {/*    label="SOC2 & Enterprise Ready"*/}
              {/*    size="small"*/}
              {/*    sx={{ bgcolor: 'rgba(57, 214, 176, 0.08)', color: '#94A3B8', border: '1px solid rgba(57, 214, 176, 0.25)', fontSize: '0.75rem' }}*/}
              {/*  />*/}
              {/*  <Typography variant="caption" sx={{ color: '#64748B' }}>*/}
              {/*    Trusted by high-growth engineering teams*/}
              {/*  </Typography>*/}
              {/*</Box>*/}
            </Box>
          </Grid>

          {/* ── Right Form Area ── */}
          <Grid item xs={12} md={6} lg={5.5}>
            {/* Mobile Header Logo (< 900px) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 3 }}>
              <Box component="img" src="/static/logo.svg" alt="KodeGauge" sx={{ height: 34, filter: 'brightness(0) invert(1)' }} />
            </Box>

            {/* Form Card Container */}
            <Box
              sx={{
                background: 'linear-gradient(145deg, rgba(16, 42, 70, 0.85) 0%, rgba(10, 28, 48, 0.90) 100%)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(120, 180, 220, 0.16)',
                borderRadius: '20px',
                p: { xs: 3, sm: 4.5 },
                boxShadow: '0 20px 48px -10px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.04)',
                animation: 'fadeInScale 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '@keyframes fadeInScale': {
                  from: { opacity: 0, transform: 'scale(0.98) translateY(8px)' },
                  to: { opacity: 1, transform: 'scale(1) translateY(0)' },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              <Outlet />
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};